import * as THREE from "three";

import type {
  AircraftArchitectureRenderBody,
  AircraftArchitectureRenderPayload,
  AircraftArchitectureTransform,
} from "./AircraftArchitectureGeometryScene";
import { resolveLoomControlSurfaceGeometry, type LoomPoint3 } from "./loomControlSurfaceGeometry";
import { createDefaultLoomWingBodyScene } from "./loomAuthoringModel";
import {
  loadLoomNativeHybridAsset,
  type LoomVerifiedNativeHybridAsset,
} from "./loomNativeHybridAsset";
import { createDefaultLoomWingControlSurface } from "./loomWingControlSurfaceContract";

const SOURCE_TO_AIRCRAFT_FRAME = new THREE.Matrix4().makeScale(-1, 1, 1);

function matrixTuple(matrix: THREE.Matrix4): AircraftArchitectureTransform {
  return matrix.toArray() as AircraftArchitectureTransform;
}

function acceptedFacesForSource(
  asset: LoomVerifiedNativeHybridAsset,
  sourceNodeId: string,
): Uint32Array {
  const sourceIndex = asset.acceptedGeometry.header.sourceNodes.findIndex(
    (source) => source.sourceNodeId === sourceNodeId,
  );
  if (sourceIndex < 0) throw new Error(`Verified accepted mesh has no ${sourceNodeId} source owner`);
  const selected: number[] = [];
  for (let face = 0; face < asset.acceptedGeometry.faceSourceNodeIndices.length; face += 1) {
    if (asset.acceptedGeometry.faceSourceNodeIndices[face] !== sourceIndex) continue;
    selected.push(
      asset.acceptedGeometry.faces[3 * face]!,
      asset.acceptedGeometry.faces[3 * face + 1]!,
      asset.acceptedGeometry.faces[3 * face + 2]!,
    );
  }
  if (!selected.length) throw new Error(`Verified accepted mesh has no faces owned by ${sourceNodeId}`);
  return new Uint32Array(selected);
}

function mirrored(point: LoomPoint3): LoomPoint3 {
  return [point[0], -point[1], point[2]];
}

function controlLoftBody(
  side: "port" | "starboard",
): AircraftArchitectureRenderBody {
  const document = createDefaultLoomWingBodyScene("aircraft-architecture-control-preview");
  const wing = document.nodes.find((node) => node.id === "wing" && node.type === "primitive");
  if (!wing || wing.type !== "primitive" || wing.definition.primitive !== "wing") {
    throw new Error("Canonical Loom wing definition is unavailable for the control preview");
  }
  const control = createDefaultLoomWingControlSurface(
    "architecture-aileron",
    "Architecture aileron preview",
  );
  const resolved = resolveLoomControlSurfaceGeometry(wing.definition, control);
  const sidePoint = (point: LoomPoint3) => side === "port" ? mirrored(point) : point;
  const hingeStart = sidePoint(resolved.hinge.start);
  const positions: number[] = [];
  for (const station of resolved.stations) {
    for (const source of station.ring) {
      const point = sidePoint(source);
      positions.push(
        point[0] - hingeStart[0],
        point[1] - hingeStart[1],
        point[2] - hingeStart[2],
      );
    }
  }
  const indices: number[] = [];
  for (let station = 0; station < resolved.stations.length - 1; station += 1) {
    for (let edge = 0; edge < 4; edge += 1) {
      const next = (edge + 1) % 4;
      const a = station * 4 + edge;
      const b = station * 4 + next;
      const c = (station + 1) * 4 + edge;
      const d = (station + 1) * 4 + next;
      indices.push(a, c, b, b, c, d);
    }
  }
  for (const station of [0, resolved.stations.length - 1]) {
    const base = station * 4;
    indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
  const sourceTransform = new THREE.Matrix4().makeTranslation(...hingeStart);
  const transform = SOURCE_TO_AIRCRAFT_FRAME.clone().multiply(sourceTransform);
  return {
    geometryAuthority: "adapted-preview-only",
    indices: new Uint32Array(indices),
    ownerId: `aircraft/main-wing/control-${side}`,
    positions: new Float32Array(positions),
    role: side === "port" ? "movable-port" : "movable-starboard",
    transform: matrixTuple(transform),
  };
}

export function adaptVerifiedLoomAssetToAircraftArchitecturePayload(
  asset: LoomVerifiedNativeHybridAsset,
): AircraftArchitectureRenderPayload {
  if (asset.integrity.status !== "verified"
      || asset.header.coordinateSystem.axes !== "x aft, y starboard, z up"
      || asset.header.coordinateSystem.lengthUnit !== "m"
      || asset.acceptedGeometry.header.status !== "accepted") {
    throw new Error("Aircraft architecture rendering requires a verified accepted Loom mesh in the canonical source frame");
  }
  const wingFaces = acceptedFacesForSource(asset, "wing");
  const fixedSource: AircraftArchitectureRenderBody = {
    geometryAuthority: "verified-accepted-mesh",
    indices: asset.acceptedGeometry.faces,
    ownerId: "aircraft/fixed/source-wing-body",
    positions: asset.acceptedGeometry.positions,
    role: "fixed",
    transform: matrixTuple(SOURCE_TO_AIRCRAFT_FRAME),
  };
  const reflectedWing: AircraftArchitectureRenderBody = {
    geometryAuthority: "verified-accepted-mesh",
    indices: wingFaces,
    ownerId: "aircraft/fixed/reflected-wing-occurrence",
    positions: asset.acceptedGeometry.positions,
    role: "fixed",
    transform: matrixTuple(SOURCE_TO_AIRCRAFT_FRAME.clone().multiply(new THREE.Matrix4().makeScale(1, -1, 1))),
  };
  return {
    artifactVersionId: `local-fixture:loom-native-hybrid-v1:${asset.header.payloadSha256}`,
    bodies: [fixedSource, reflectedWing, controlLoftBody("port"), controlLoftBody("starboard")],
    geometryProvenance: "verified-loom-accepted-mesh",
    sceneComposition: "verified-source-plus-explicit-reflection",
    topologyRevision: asset.acceptedGeometry.header.topologyRevision,
  };
}

let verifiedPayloadPromise: Promise<AircraftArchitectureRenderPayload> | null = null;

export function loadAircraftArchitectureRenderPayload(): Promise<AircraftArchitectureRenderPayload> {
  if (!verifiedPayloadPromise) {
    verifiedPayloadPromise = loadLoomNativeHybridAsset()
      .then(adaptVerifiedLoomAssetToAircraftArchitecturePayload)
      .catch((error: unknown) => {
        verifiedPayloadPromise = null;
        throw error;
      });
  }
  return verifiedPayloadPromise;
}
